import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { CheckCircle, Clock, X, DownloadSimple, ClipboardText, UploadSimple, ArrowRight, ArrowLeft, FileText } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api, { uploadProcessedFile, getAgencies } from '../../lib/api';

const fmt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');

export default function SubmitReportDialog({ open, onOpenChange, fileId, fileName, rows = [] }) {
  const submitted = rows.filter(r => r.submitted_at);
  const success = submitted.filter(r => r.status === 'success');
  const pending = submitted.filter(r => r.status === 'pending');
  const failed = submitted.filter(r => r.status === 'failed');
  const billable = success.length + pending.length;
  const totalAmt = submitted.reduce((t, r) => t + Number(r.amount || 0), 0);
  const billableAmt = [...success, ...pending].reduce((t, r) => t + Number(r.amount || 0), 0);

  // ── 2-step wizard state ──────────────────────────────────────────────────
  const [step, setStep] = useState(1); // 1 = download summary, 2 = upload report
  const [agencies, setAgencies] = useState([]);
  const [agencyId, setAgencyId] = useState('');
  const [quantity, setQuantity] = useState(String(billable || ''));
  const [source, setSource] = useState('tataneu');
  const [uploadFile, setUploadFile] = useState(null);
  const [submittingUpload, setSubmittingUpload] = useState(false);

  // Reset when dialog opens
  useEffect(() => {
    if (open) {
      setStep(1);
      setUploadFile(null);
      setQuantity(String(billable || ''));
      getAgencies().then(({ data }) => setAgencies(data || [])).catch(() => setAgencies([]));
    }
  }, [open, billable]);

  const downloadExcel = async () => {
    try {
      const r = await api.get(`/booking-execution/${fileId}/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `${fileName || 'bookings'}_report.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Excel downloaded');
    } catch { toast.error('Download failed'); }
  };

  const copyWhatsAppSummary = () => {
    const lines = [
      `*📊 Booking Report — ${fileName || 'File'}*`,
      `Total: ${submitted.length}`,
      `✅ Success: ${success.length}`,
      `⏳ Pending: ${pending.length}`,
      `❌ Failed: ${failed.length}`,
      `💰 Amount: ${fmt(totalAmt)}`,
      '',
    ];
    if (failed.length > 0) {
      lines.push('*❌ Failed:*');
      failed.slice(0, 20).forEach(r => lines.push(`• ${r.consumer_id} — ${r.notes || 'no reason'}`));
      if (failed.length > 20) lines.push(`...+${failed.length - 20} more`);
    }
    lines.push('');
    lines.push(`*🧾 Billable (✓+⏳): ${billable} cyl — ${fmt(billableAmt)}*`);
    try {
      navigator.clipboard.writeText(lines.join('\n'));
      toast.success('Copied for WhatsApp');
    } catch { toast.error('Copy failed'); }
  };

  const handleStep2Upload = async () => {
    if (!uploadFile) { toast.error('Report file select karo'); return; }
    if (!agencyId) { toast.error('Agency select karo'); return; }
    const qty = Number(quantity);
    if (!qty || qty <= 0) { toast.error('Quantity dalo (1 ya usse zyada)'); return; }
    setSubmittingUpload(true);
    try {
      const fd = new FormData();
      fd.append('file', uploadFile);
      fd.append('agency_id', agencyId);
      fd.append('quantity', String(qty));
      fd.append('source', source);
      await uploadProcessedFile(fileId, fd);
      toast.success('Report uploaded — distributor wallet debit ho gaya!');
      onOpenChange(false);
      // Reload page so workspace shows the file as Completed.
      setTimeout(() => { window.location.href = '/assigned-files'; }, 1000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload fail hua');
    } finally {
      setSubmittingUpload(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="submit-report-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {step === 1 ? '📊 Step 1 of 2 — Booking Report' : '📤 Step 2 of 2 — Upload Final Report'} — {fileName}
          </DialogTitle>
        </DialogHeader>

        {/* Step indicator */}
        <div className="flex items-center gap-2 text-xs mb-2" data-testid="report-step-indicator">
          <span className={`px-2 py-1 rounded font-bold ${step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'}`}>1. Report Dekho</span>
          <ArrowRight size={12} />
          <span className={`px-2 py-1 rounded font-bold ${step >= 2 ? 'bg-violet-600 text-white' : 'bg-gray-200 text-gray-500'}`}>2. Upload → Wallet Debit</span>
        </div>

        {step === 1 && (<>
          {/* Counts */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <div className="bg-violet-50 border-2 border-violet-300 rounded p-2 text-center" data-testid="billable-count" title="Agency ko bill hogi — Success + Pending dono count">
              <div className="text-[10px] text-violet-700 uppercase font-bold">Billable</div>
              <div className="text-2xl font-extrabold text-violet-900">{billable}</div>
              <div className="text-[10px] text-violet-700 font-semibold">{fmt(billableAmt)}</div>
              <div className="text-[9px] text-violet-600 mt-0.5">✓ + ⏳</div>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-center" data-testid="success-count">
              <CheckCircle size={18} weight="fill" className="text-emerald-600 mx-auto mb-1" />
              <div className="text-xl font-bold text-emerald-800">{success.length}</div>
              <div className="text-[10px] text-emerald-700 uppercase">Success</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded p-2 text-center" data-testid="pending-count">
              <Clock size={18} weight="fill" className="text-amber-600 mx-auto mb-1" />
              <div className="text-xl font-bold text-amber-800">{pending.length}</div>
              <div className="text-[10px] text-amber-700 uppercase">Pending</div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded p-2 text-center" data-testid="failed-count">
              <X size={18} weight="bold" className="text-red-600 mx-auto mb-1" />
              <div className="text-xl font-bold text-red-800">{failed.length}</div>
              <div className="text-[10px] text-red-700 uppercase">Failed</div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded p-2 text-center">
              <div className="text-xl font-bold text-blue-800">{fmt(totalAmt)}</div>
              <div className="text-[10px] text-blue-700 uppercase">Total Amt</div>
            </div>
          </div>

          {failed.length > 0 && (
            <div className="mt-3">
              <div className="text-xs font-bold text-red-800 mb-1">❌ Failed Rows:</div>
              <div className="overflow-x-auto max-h-60">
                <table className="w-full text-xs">
                  <thead className="bg-red-50 sticky top-0">
                    <tr>
                      <th className="px-2 py-1 text-left">Consumer ID</th>
                      <th className="px-2 py-1 text-left">Mobile</th>
                      <th className="px-2 py-1 text-right">Amount</th>
                      <th className="px-2 py-1 text-left">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {failed.slice(0, 50).map(r => (
                      <tr key={r.id} className="border-t">
                        <td className="px-2 py-1 font-mono">{r.consumer_id}</td>
                        <td className="px-2 py-1">{r.mobile || '-'}</td>
                        <td className="px-2 py-1 text-right">{fmt(r.amount)}</td>
                        <td className="px-2 py-1 text-red-700">{r.notes || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {failed.length > 50 && <div className="text-[10px] text-gray-500 mt-1">Showing first 50. Download Excel for all.</div>}
              </div>
            </div>
          )}

          <DialogFooter className="flex-wrap gap-2">
            <Button variant="outline" onClick={copyWhatsAppSummary} data-testid="copy-summary-btn">
              <ClipboardText size={14} /> Copy WhatsApp Summary
            </Button>
            <Button onClick={downloadExcel} className="bg-emerald-600 hover:bg-emerald-700" data-testid="download-excel-btn">
              <DownloadSimple size={14} /> Download Excel
            </Button>
            <Button onClick={() => setStep(2)} className="bg-violet-600 hover:bg-violet-700 gap-1 font-bold" data-testid="goto-step2-btn">
              Next: Upload Report <ArrowRight size={14} />
            </Button>
          </DialogFooter>
        </>)}

        {step === 2 && (<>
          <div className="bg-amber-50 border border-amber-300 rounded p-3 text-xs text-amber-900 mb-2">
            ⚠️ <b>Wallet debit hone ke liye final report upload zaroori hai.</b> Agency select karo, quantity dalo, file upload karo. Tab tak distributor wallet se paisa nahi katega.
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Agency *</Label>
              <select value={agencyId} onChange={e => setAgencyId(e.target.value)} className="w-full h-9 border rounded px-2 text-sm" data-testid="step2-agency-select">
                <option value="">— Select Agency —</option>
                {agencies.map(a => <option key={a.id} value={a.id}>{a.name} ({a.oil_company})</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Quantity (success count) *</Label>
              <Input type="number" value={quantity} onChange={e => setQuantity(e.target.value)} placeholder={String(billable)} className="h-9 text-sm" data-testid="step2-quantity-input" />
              <div className="text-[10px] text-gray-500 mt-0.5">Workspace billable: <b>{billable}</b></div>
            </div>
            <div>
              <Label className="text-xs">Payment Source *</Label>
              <select value={source} onChange={e => setSource(e.target.value)} className="w-full h-9 border rounded px-2 text-sm" data-testid="step2-source-select">
                <option value="tataneu">📱 TataNeu</option>
                <option value="jiofinance">📱 JioFinance</option>
                <option value="cash">💵 Cash / Normal</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Final Report File *</Label>
              <Input type="file" accept=".xlsx,.xls,.csv,.pdf" onChange={e => setUploadFile(e.target.files?.[0] || null)} className="h-9 text-sm" data-testid="step2-file-input" />
              {uploadFile && <div className="text-[10px] text-emerald-700 mt-0.5"><FileText size={10} className="inline" /> {uploadFile.name}</div>}
            </div>
          </div>

          <DialogFooter className="flex-wrap gap-2 mt-3">
            <Button variant="outline" onClick={() => setStep(1)} data-testid="back-to-step1-btn">
              <ArrowLeft size={14} /> Back
            </Button>
            <Button onClick={handleStep2Upload} disabled={submittingUpload} className="bg-violet-600 hover:bg-violet-700 font-bold" data-testid="step2-submit-upload-btn">
              <UploadSimple size={14} /> {submittingUpload ? 'Uploading…' : 'Upload & Debit Wallet'}
            </Button>
          </DialogFooter>
        </>)}
      </DialogContent>
    </Dialog>
  );
}
